import { PaymentFormData } from "@/components/Payment/PaymentForm/interfaces/PaymentFormData.ts";
import { isInquiryPaymentType } from "@/helpers/isInquiryPaymentType.ts";
import { useScrollToTop } from "@/helpers/scrollToTop.ts";
import { PaymentType } from "@/helpers/types/types.ts";
import { useCartDrawerState } from "@/helpers/useCartDrawerState.ts";
import { useGetOrganization } from "@/helpers/useGetOrganization.tsx";
import { HolderStepValidatedData } from "@/pages/BookingEngine/hooks/useTryHolderFormSubmit.tsx";
import { useReservationSubmit } from "@/pages/ReservationOverview/useReservationSubmit.tsx";
import { useEngineStore } from "@/stores/EngineStore/EngineStore.ts";
import { AppPage } from "@/stores/EngineStore/enums/AppPage.ts";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

export const useMobileDrawer = (
  onSubmitComplete: () => void,
  tryHolderFormSubmit: () => Promise<HolderStepValidatedData | null>,
  paymentFormData: PaymentFormData,
) => {
  const { t } = useTranslation();
  const { handleOpenCartDrawer, handleCloseCartDrawer } = useCartDrawerState();
  const { handleSubmitCart } = useReservationSubmit(onSubmitComplete);
  const { hasEmbeddedCardForm } = useGetOrganization();
  const scrollToTop = useScrollToTop();

  const {
    page,
    setPage,
    setReservationHolderData,
    calculatedCartData,
    acceptedGDPR,
    setIsGDPRHighlighted,
    setPaymentData,
  } = useEngineStore(
    useShallow((state) => ({
      page: state.page,
      setPage: state.setPage,
      setReservationHolderData: state.setReservationHolderData,
      calculatedCartData: state.calculatedCartData,
      acceptedGDPR: state.acceptedGDPR,
      setIsGDPRHighlighted: state.setIsGDPRHighlighted,
      setPaymentData: state.setPaymentData,
    })),
  );

  const isForcedInquiry = isInquiryPaymentType(calculatedCartData);
  const isCardGuarantee = calculatedCartData?.paymentConfig?.paymentType === PaymentType.CardGuarantee;

  const getMobileBtnText = () => {
    switch (page) {
      case AppPage.AccommodationPicking:
      case AppPage.ExtraOptions:
      case AppPage.HolderAndPaymentInfo:
        return t("continueShort");
      case AppPage.ReservationOverview:
        return isForcedInquiry ? t("sendInquiry") : t("finish");
      default:
        return t("continueShort");
    }
  };

  const handleMobileBack = () => {
    switch (page) {
      case AppPage.ExtraOptions:
        setPage(AppPage.AccommodationPicking);
        handleCloseCartDrawer();
        scrollToTop();
        break;
      case AppPage.HolderAndPaymentInfo:
        setPage(AppPage.ExtraOptions);
        handleCloseCartDrawer();
        scrollToTop();
        break;
      case AppPage.ReservationOverview:
        setPage(AppPage.HolderAndPaymentInfo);
        handleCloseCartDrawer();
        scrollToTop();
        break;
      default:
        return;
    }
  };

  return {
    handleMobileBack,
    handleMobileContinue: async () => {
      switch (page) {
        case AppPage.AccommodationPicking:
          setPage(AppPage.ExtraOptions);
          handleCloseCartDrawer();
          scrollToTop();
          break;
        case AppPage.ExtraOptions:
          setPage(AppPage.HolderAndPaymentInfo);
          handleCloseCartDrawer();
          scrollToTop();
          break;
        case AppPage.HolderAndPaymentInfo: {
          const result = await tryHolderFormSubmit();
          if (!result) return;
          setReservationHolderData(result.holder);
          if (!isForcedInquiry && hasEmbeddedCardForm && isCardGuarantee && result.payment) {
            paymentFormData.onSubmitPaymentForm(result.payment);
          } else {
            setPaymentData(undefined);
          }
          setPage(AppPage.ReservationOverview);
          handleCloseCartDrawer();
          scrollToTop();
          break;
        }
        case AppPage.ReservationOverview:
          if (acceptedGDPR) {
            void handleSubmitCart();
          } else {
            handleOpenCartDrawer();
            setIsGDPRHighlighted(true);
          }
          break;
        default:
          return;
      }
    },
    handleOpenCartDrawer,
    handleCloseCartDrawer,
    getMobileBtnText,
  };
};
